import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as fromRoot from '@app/reducers';
import { decodeRegionFromId } from '@app/shared/helpers/helpers';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { errors, GetUserTransferOutput, Transfer } from '@smash-sdk/transfer/ejs/01-2024';
import { Region } from '@smash-sdk/core';
import { from, of } from 'rxjs';
import { catchError, concatMap, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { Constant } from 'src/constant';
import { TransfersSentActions } from '../../transfers-sent/actions';
import { TransferDetailsActions } from '../actions';



@Injectable()
export class TransferDetailsEffects {

  loadTransfer = createEffect(() =>
    this.actions$.pipe(
      ofType(TransferDetailsActions.loadTransfer),
      map(action => action.transferId),
      switchMap((transferId) => {
        return [TransferDetailsActions.loadTransferDetails({ transferId }), TransferDetailsActions.loadTransferDetailsFiles({ transferId }), TransferDetailsActions.loadTransferEvents({ transferId })];
      }),
    )
  );

  loadTransferDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransferDetailsActions.loadTransferDetails),
      withLatestFrom(
        this.store$.pipe(select(fromRoot.getActiveIdentity)),
      ),
      concatMap(([action, user]) => {
        const transferSdk = new Transfer();
        return from(transferSdk.getUserTransfer({ userId: user.id, transferId: action.transferId })).pipe(
          map(({ transfer }) => {
            const transformedTransfer = {
              ...transfer,
              delivery: {
                ...transfer.delivery,
                receivers: transfer?.delivery?.receivers ? transfer.delivery.receivers.map(receiver => ({ email: receiver })) : []
              }
            } as GetUserTransferOutput['transfer'] & {
              delivery: { receivers: { email: string }[] }
            };
            return TransferDetailsActions.loadTransferDetailsSuccess({ transfer: transformedTransfer });
          }),
          catchError(error => of(TransferDetailsActions.loadTransferDetailsFailure({ error: error.errorMessage }))
          ),
        );
      }),
    )
  );

  loadTransferEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransferDetailsActions.loadTransferEvents),
      withLatestFrom(
        this.store$.pipe(select(fromRoot.getActiveIdentity)),
      ),
      concatMap(([action, user]) => {
        const decodedRegion = decodeRegionFromId(action.transferId);
        const transferSdk = new Transfer({ region: decodedRegion as Region });
        return from(transferSdk.getUserTransfer({ userId: user.id, transferId: action.transferId })).pipe(
          concatMap(({ transfer }) => {
            const eventsGroupedByEmail = new Map();
            if (transfer?.delivery?.receivers) {
              const receivers = transfer?.delivery?.receivers;
              receivers.forEach(receiver => {
                eventsGroupedByEmail[receiver] = { email: receiver, views: 0, downloads: 0, events: [] };
              });
            }

            return from(transferSdk.listUserTransferEvents({ userId: user.id, transferId: action.transferId })).pipe(
              concatMap(({ events }) => {
                const eventsSorted = events.sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());
                const eventsWithEmail = eventsSorted.filter(event => event.email);


                eventsWithEmail.forEach(event => {
                  if (eventsGroupedByEmail[event.email]) {
                    if (event.type === Constant.transferEventStatus.view) {
                      ++eventsGroupedByEmail[event.email].views;
                    }
                    if (event.type === Constant.transferEventStatus.download) {
                      ++eventsGroupedByEmail[event.email].downloads;
                    }
                    eventsGroupedByEmail[event.email].events.push(event);
                  }
                });

                const eventsGroupedByAnonymousUser = new Map();
                const eventsAnonymous = events.filter(event => !event.email);

                eventsAnonymous.forEach(event => {
                  if (!eventsGroupedByAnonymousUser[event.userId]) {
                    eventsGroupedByAnonymousUser[event.userId] = { userId: event.userId, views: 0, downloads: 0, events: [] };
                  }
                  if (event.type === Constant.transferEventStatus.view) {
                    ++eventsGroupedByAnonymousUser[event.userId].views;
                  }
                  if (event.type === Constant.transferEventStatus.download) {
                    ++eventsGroupedByAnonymousUser[event.userId].downloads;
                  }
                  eventsGroupedByAnonymousUser[event.userId].events.push(event);
                });

                const eventsGroupedByEmailArray = Object.keys(eventsGroupedByEmail).map(email => eventsGroupedByEmail[email]);
                const eventsGroupedByAnonymousUserArray = Object.keys(eventsGroupedByAnonymousUser).map((userId) => ({ ...eventsGroupedByAnonymousUser[userId] }));
                return [TransferDetailsActions.loadTransferEventsSuccess({ events, eventsGroupedByEmailArray, eventsGroupedByAnonymousUserArray })];
              }
              ),
              catchError(error => {
                if (error instanceof errors.ListUserTransferEventsError.NotFoundError) {
                  const eventsGroupedByEmailArray = Object.keys(eventsGroupedByEmail).map(email => eventsGroupedByEmail[email]);
                  return [TransferDetailsActions.loadTransferEventsSuccess({ events: [], eventsGroupedByEmailArray, eventsGroupedByAnonymousUserArray: [] })];
                } else {
                  of(TransferDetailsActions.loadTransferEventsFailure({ error: error.errorMessage }));
                }
              }
              ));
          })
        );
      })
    )
  );

  loadTransferDetailsFiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransferDetailsActions.loadTransferDetailsFiles),
      withLatestFrom(
        this.store$.pipe(select(fromRoot.getActiveIdentity)),
      ),
      concatMap(([action, user]) => {
        const decodedRegion = decodeRegionFromId(action.transferId);
        const transfer = new Transfer({ region: decodedRegion as Region });
        return from(transfer.listUserTransferFiles({ userId: user.id, transferId: action.transferId })).pipe(
          map(({ files }) => {
            return TransferDetailsActions.loadTransferDetailsFilesSuccess({ transferFiles: files });
          }),
          catchError(error =>
            of(TransferDetailsActions.loadTransferDetailsFilesFailure({ error: error.errorMessage }))
          )
        )
      }
      )
    )
  );

  deleteTransfer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransferDetailsActions.deleteTransfer),
      withLatestFrom(
        this.store$.pipe(select(fromRoot.getActiveIdentity)),
      ),
      concatMap(([action, user]) => {
        const decodedRegion = decodeRegionFromId(action.transferId);
        const transfer = new Transfer({ region: decodedRegion as Region });
        return from(transfer.deleteUserTransfer({ userId: user.id, transferId: action.transferId })).pipe(
          concatMap(({ transfer }) => {
            return [
              TransferDetailsActions.deleteTransferSuccess({ transferId: transfer.id }),
              TransfersSentActions.loadTransfersSent(),
            ];
          }
          ),
          catchError(error =>
            of(TransferDetailsActions.deleteTransferFailure({ error: error.errorMessage }))
          )
        )
      }
      )
    )
  );

  deleteTransferSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransferDetailsActions.deleteTransferSuccess),
      withLatestFrom(
        this.store$.select(fromRoot.getActiveTeam),
      ),
      tap(([action, activeTeam]) => {
        this.router.navigateByUrl('/account/team/' + activeTeam.id + '/transfers/sent');
      })
    ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private store$: Store<fromRoot.State>,
    private router: Router,
  ) { }

}
